/**
 * Page de gestion des livreurs
 * Permet d'ajouter, modifier et gérer l'équipe de livraison
 */
import { useState, useEffect } from 'react';
import { Truck, Plus, Edit2, Trash2, Phone, Mail, CheckCircle, XCircle } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

interface Driver {
  id: string;
  name: string;
  email: string;
  phone?: string;
  vehicle_type?: string;
  vehicle_plate?: string;
  active: boolean;
  created_at: string;
}

export default function DriversManagement() {
  const { tenant } = useTenant();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });

  useEffect(() => {
    if (tenant) {
      loadDrivers();
    }
  }, [tenant]);

  const loadDrivers = async () => {
    if (!tenant) return;
    try {
      // TODO: Create proper API endpoint for drivers
      const response = await fetch(`${import.meta.env.VITE_API_URL}/drivers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-ID': tenant.id,
        },
      });

      if (!response.ok) throw new Error('Failed to load drivers');

      const data = await response.json();
      setDrivers(data);
    } catch (error) {
      console.error('Error loading drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant) return;

    try {
      const url = editingDriver
        ? `${import.meta.env.VITE_API_URL}/drivers/${editingDriver.id}`
        : `${import.meta.env.VITE_API_URL}/drivers`;

      const method = editingDriver ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-ID': tenant.id,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to save driver');

      await loadDrivers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving driver:', error);
      alert('Erreur lors de l\'enregistrement du livreur');
    }
  };

  const handleDelete = async (driverId: string) => {
    if (!tenant) return;
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce livreur ?')) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/drivers/${driverId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-ID': tenant.id,
        },
      });

      if (!response.ok) throw new Error('Failed to delete driver');

      await loadDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      alert('Erreur lors de la suppression du livreur');
    }
  };

  const handleToggleActive = async (driver: Driver) => {
    if (!tenant) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/drivers/${driver.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-ID': tenant.id,
        },
        body: JSON.stringify({ active: !driver.active }),
      });

      if (!response.ok) throw new Error('Failed to toggle driver status');

      await loadDrivers();
    } catch (error) {
      console.error('Error toggling driver status:', error);
      alert('Erreur lors de la modification du statut');
    }
  };

  const handleOpenModal = (driver?: Driver) => {
    if (driver) {
      setEditingDriver(driver);
      setFormData({
        name: driver.name,
        email: driver.email,
        phone: driver.phone || '',
        password: '',
      });
    } else {
      setEditingDriver(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDriver(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-7 h-7 text-blue-600" />
            Gestion des Livreurs
          </h1>
          <p className="text-gray-600 mt-1">
            {drivers.length} livreur{drivers.length > 1 ? 's' : ''} • {drivers.filter(d => d.active).length} actif{drivers.filter(d => d.active).length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un livreur
        </button>
      </div>

      {/* Drivers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {drivers.map((driver) => (
          <div
            key={driver.id}
            className={`bg-white rounded-lg shadow-md p-5 border-2 ${
              driver.active ? 'border-green-200' : 'border-gray-200'
            } hover:shadow-lg transition-shadow`}
          >
            {/* Status Badge */}
            <div className="flex items-center justify-between mb-3">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                driver.active
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {driver.active ? (
                  <><CheckCircle className="w-4 h-4" /> Actif</>
                ) : (
                  <><XCircle className="w-4 h-4" /> Inactif</>
                )}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleOpenModal(driver)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(driver.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Driver Info */}
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-gray-900">{driver.name}</h3>

              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Mail className="w-4 h-4" />
                <span>{driver.email}</span>
              </div>

              {driver.phone && (
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>{driver.phone}</span>
                </div>
              )}
            </div>

            {/* Toggle Active Button */}
            <button
              onClick={() => handleToggleActive(driver)}
              className={`w-full mt-4 py-2 rounded-lg font-medium transition-colors ${
                driver.active
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {driver.active ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {drivers.length === 0 && (
        <div className="text-center py-12">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun livreur</h3>
          <p className="text-gray-600 mb-6">Commencez par ajouter votre premier livreur</p>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Ajouter un livreur
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingDriver ? 'Modifier le livreur' : 'Nouveau livreur'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {!editingDriver && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe *
                  </label>
                  <input
                    type="password"
                    required={!editingDriver}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingDriver ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
